import React from 'react';
import styled from 'styled-components';

import { Box } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { useAdminGetDashboardTotalEarningsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardTotalEarningsDataQuery';
import { useAdminGetDashboardAppRevenueDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardAppRevenueDataQuery';
import { formatNumberWithCommas } from '../../utils/converters';
import { calculatePercentageChange } from '../../utils/dashboard';
import { CardError } from './CardError';

type Props = {
  fromDate: string | null;
  toDate: string | null;
  priorFrom: string | null;
  priorTo: string | null;
  selectedLabel: string;
};

const TotalRevenueCard: React.FC<Props> = ({
  fromDate,
  toDate,
  priorFrom,
  priorTo,
  selectedLabel,
}) => {
  const { data, isFetching, isError, refetch } = useAdminGetDashboardTotalEarningsDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    priorFromDate: priorFrom || '',
    priorToDate: priorTo || '',
  });

  const {
    data: iosRevenueData,
    isFetching: iosIsFetching,
    isError: iosIsError,
    refetch: iosRefetch,
  } = useAdminGetDashboardAppRevenueDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    platform: 'ios',
  });

  const {
    data: androidRevenueData,
    isFetching: androidIsFetching,
    isError: androidIsError,
    refetch: androidRefetch,
  } = useAdminGetDashboardAppRevenueDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    platform: 'android',
  });

  const {
    data: stripeRevenueData,
    isFetching: stripeIsFetching,
    isError: stripeIsError,
    refetch: stripeRefetch,
  } = useAdminGetDashboardAppRevenueDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    platform: 'stripe',
  });

  const totalRevenue = React.useMemo(
    () =>
      (data?.totalCollabsCost || 0) +
      (data?.totalCreditsPurchased || 0) +
      (data?.totalPremiumModels || 0) +
      (data?.totalPremiumVenues || 0) +
      (data?.totalReservationsCost || 0) +
      (data?.totalStarterBrands || 0),
    [data],
  );

  const totalPriorRevenue = React.useMemo(
    () =>
      (data?.totalPriorCollabsCost || 0) +
      (data?.totalPriorCreditsPurchased || 0) +
      (data?.totalPriorPremiumModels || 0) +
      (data?.totalPriorPremiumVenues || 0) +
      (data?.totalPriorReservationsCost || 0) +
      (data?.totalPriorStarterBrands || 0),
    [data],
  );

  const {
    totalPercentage,
    premiumModelsPercentage,
    totalCreditsPurchasedPercentage,
    totalPremiumVenuesPercentage,
    totalPremiumBrandsPercentage,
    totalReservationCostPercentage,
    totalCollabsPercentage,
  } = React.useMemo(() => {
    const totalPercentage = calculatePercentageChange(totalRevenue, totalPriorRevenue);
    const premiumModelsPercentage = calculatePercentageChange(
      data?.totalPremiumModels || 0,
      data?.totalPriorPremiumModels || 0,
    );
    const totalCreditsPurchasedPercentage = calculatePercentageChange(
      data?.totalCreditsPurchased || 0,
      data?.totalPriorCreditsPurchased || 0,
    );
    const totalPremiumVenuesPercentage = calculatePercentageChange(
      data?.totalPremiumVenues || 0,
      data?.totalPriorPremiumVenues || 0,
    );

    const totalPremiumBrandsPercentage = calculatePercentageChange(
      data?.totalStarterBrands || 0,
      data?.totalPriorStarterBrands || 0,
    );

    const totalCollabsPercentage = calculatePercentageChange(
      data?.totalCollabsCost || 0,
      data?.totalPriorCollabsCost || 0,
    );

    const totalReservationCostPercentage = calculatePercentageChange(
      data?.totalReservationsCost || 0,
      data?.totalPriorReservationsCost || 0,
    );

    return {
      totalPercentage,
      premiumModelsPercentage,
      totalCreditsPurchasedPercentage,
      totalPremiumVenuesPercentage,
      totalPremiumBrandsPercentage,
      totalCollabsPercentage,
      totalReservationCostPercentage,
    };
  }, [data]);

  return (
    <CardContainer>
      <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
        <Box>
          <Name>Total revenue</Name>
          <Time>{selectedLabel}</Time>
        </Box>
        <Box>
          <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
            {isFetching && (
              <Skeleton
                animation="wave"
                variant="rect"
                width={120}
                height={30}
                style={{ marginTop: '5px', borderRadius: '5px' }}
              />
            )}
          </Box>
          {!isFetching && data && (
            <Box display={'flex'} flexDirection={'column'}>
              <Details>
                <p>${formatNumberWithCommas(totalRevenue)}</p>
              </Details>
              <Details isNegative={totalPercentage}>
                <span>{totalPercentage}%</span>
              </Details>
            </Box>
          )}
        </Box>
      </Box>
      {isError && !isFetching && <CardError onRetry={() => refetch()} />}
      <SummaryCardGridDesktopStats>
        <DetailedContainer>
          <Name>Premium models</Name>
          {isFetching && (
            <Skeleton
              animation="wave"
              variant="rect"
              width={60}
              height={20}
              style={{ borderRadius: '5px' }}
            />
          )}
          {!isFetching && data && (
            <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
              <Amount>${formatNumberWithCommas(data?.totalPremiumModels || 0)}</Amount>
              <PercentageAmount isNegative={premiumModelsPercentage}>
                {premiumModelsPercentage}%
              </PercentageAmount>
            </Box>
          )}
        </DetailedContainer>
        <DetailedContainer>
          <Name>Credits purchased models</Name>
          {isFetching && (
            <Skeleton
              animation="wave"
              variant="rect"
              width={60}
              height={20}
              style={{ borderRadius: '5px' }}
            />
          )}
          {!isFetching && data && (
            <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
              <Amount>${formatNumberWithCommas(data?.totalCreditsPurchased || 0)}</Amount>
              <PercentageAmount isNegative={totalCreditsPurchasedPercentage}>
                {totalCreditsPurchasedPercentage}%
              </PercentageAmount>
            </Box>
          )}
        </DetailedContainer>
        <DetailedContainer>
          <Name>Premium venues</Name>
          {isFetching && (
            <Skeleton
              animation="wave"
              variant="rect"
              width={60}
              height={20}
              style={{ borderRadius: '5px' }}
            />
          )}
          {!isFetching && data && (
            <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
              <Amount>${formatNumberWithCommas(data?.totalPremiumVenues || 0)}</Amount>
              <PercentageAmount isNegative={totalPremiumVenuesPercentage}>
                {totalPremiumVenuesPercentage}%
              </PercentageAmount>
            </Box>
          )}
        </DetailedContainer>
        <DetailedContainer>
          <Name>Premium brands</Name>
          {isFetching && (
            <Skeleton
              animation="wave"
              variant="rect"
              width={60}
              height={20}
              style={{ borderRadius: '5px' }}
            />
          )}
          {!isFetching && data && (
            <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
              <Amount>${formatNumberWithCommas(data?.totalStarterBrands || 0)}</Amount>
              <PercentageAmount isNegative={totalPremiumBrandsPercentage}>
                {totalPremiumBrandsPercentage}%
              </PercentageAmount>
            </Box>
          )}
        </DetailedContainer>
        <DetailedContainer>
          <Name>Collabs</Name>
          {isFetching && (
            <Skeleton
              animation="wave"
              variant="rect"
              width={60}
              height={20}
              style={{ borderRadius: '5px' }}
            />
          )}
          {!isFetching && data && (
            <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
              <Amount>${formatNumberWithCommas(data?.totalCollabsCost || 0)}</Amount>
              <PercentageAmount isNegative={totalCollabsPercentage}>
                {totalCollabsPercentage}%
              </PercentageAmount>
            </Box>
          )}
        </DetailedContainer>
        <DetailedContainer>
          <Name>Content</Name>
          {isFetching && (
            <Skeleton
              animation="wave"
              variant="rect"
              width={60}
              height={20}
              style={{ borderRadius: '5px' }}
            />
          )}
          {!isFetching && data && (
            <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
              <Amount>${formatNumberWithCommas(data?.totalReservationsCost || 0)}</Amount>
              <PercentageAmount isNegative={totalReservationCostPercentage}>
                {totalReservationCostPercentage}%
              </PercentageAmount>
            </Box>
          )}
        </DetailedContainer>
      </SummaryCardGridDesktopStats>

      <PlatformRevenueSection>
        <SectionTitle>Revenue by Platform</SectionTitle>
        <SummaryCardGridDesktopStats>
          <DetailedContainer>
            <Name>iOS Revenue</Name>
            {iosIsFetching && (
              <Skeleton
                animation="wave"
                variant="rect"
                width={60}
                height={20}
                style={{ borderRadius: '5px' }}
              />
            )}
            {iosIsError && !iosIsFetching && <CardError compact onRetry={() => iosRefetch()} />}
            {!iosIsFetching && iosRevenueData && (
              <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
                <Amount>${formatNumberWithCommas(iosRevenueData?.totalRevenue || 0)}</Amount>
              </Box>
            )}
          </DetailedContainer>
          <DetailedContainer>
            <Name>Android Revenue</Name>
            {androidIsFetching && (
              <Skeleton
                animation="wave"
                variant="rect"
                width={60}
                height={20}
                style={{ borderRadius: '5px' }}
              />
            )}
            {androidIsError && !androidIsFetching && (
              <CardError compact onRetry={() => androidRefetch()} />
            )}
            {!androidIsFetching && androidRevenueData && (
              <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
                <Amount>${formatNumberWithCommas(androidRevenueData?.totalRevenue || 0)}</Amount>
              </Box>
            )}
          </DetailedContainer>
          <DetailedContainer>
            <Name>Stripe Revenue</Name>
            {stripeIsFetching && (
              <Skeleton
                animation="wave"
                variant="rect"
                width={60}
                height={20}
                style={{ borderRadius: '5px' }}
              />
            )}
            {stripeIsError && !stripeIsFetching && (
              <CardError compact onRetry={() => stripeRefetch()} />
            )}
            {!stripeIsFetching && stripeRevenueData && (
              <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
                <Amount>${formatNumberWithCommas(stripeRevenueData?.totalRevenue || 0)}</Amount>
              </Box>
            )}
          </DetailedContainer>
        </SummaryCardGridDesktopStats>
      </PlatformRevenueSection>
    </CardContainer>
  );
};

export default TotalRevenueCard;

const DetailedContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #0000000d;
  padding: 10px 0;
`;

const Name = styled.div`
  color: #111111;
  font-size: 14px;
  font-family: Poppins;
  font-weight: 500;
`;

const Amount = styled.div`
  color: #408155;
  font-size: 14px;
  font-family: Poppins;
  font-weight: 500;
`;

const PercentageAmount = styled.div<{ isNegative?: number }>`
  color: ${(props) => ((props.isNegative || 0) < 0 ? '#BC3438' : '#0b7a3c')};
  font-size: 14px;
  font-weight: 400;
`;

const CardContainer = styled.div`
  border: 1px solid #0000000d;
  padding: 48px 40px;
  border-radius: 8px;
  margin-top: 100px;
  margin-bottom: 50px;
`;

const SummaryCardGridDesktopStats = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  margin-top: 47px;
`;

const Details = styled.div<{ isNegative?: number }>`
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-top: 4px;
  justify-content: flex-end;

  p {
    color: #408155;
    font-size: 36px;
    font-family: Poppins;
    font-weight: 600;
    margin: 0;
  }

  span {
    color: ${(props) => ((props.isNegative || 0) < 0 ? '#BC3438' : '#0b7a3c')};
    font-size: 12px;
    font-family: Poppins;
    font-weight: 400;
  }
`;

const Time = styled.div`
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
  font-family: Poppins;
  font-weight: 500;
`;

const PlatformRevenueSection = styled.div`
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid #0000000d;
`;

const SectionTitle = styled.h3`
  color: #111111;
  font-size: 18px;
  font-family: Poppins;
  font-weight: 600;
  margin: 0 0 20px 0;
`;
